import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { WebLlmProvider } from '../components/WebLlmProvider';
import { CollaborationProvider } from '../contexts/CollaborationContext';
import { PageHeaderProvider, usePageHeader } from '../contexts/PageHeaderContext';
import { PersonalizationProvider } from '../contexts/PersonalizationContext';
import { NotificationProvider } from '../mawi-ui';
import TopNavBar from '../components/layout/TopNavBar';
import LeftSidebar from '../components/layout/LeftSidebar';
import StatusBar from '../components/layout/StatusBar';
import ChatPanel from '../components/layout/ChatPanel';
import Chat from '../components/chat/Chat';
import FloatingChatButton from '../components/layout/FloatingChatButton';

const ThemeBuilder = lazy(() => import('../components/settings/ThemeBuilder'));
const EditorLayout = lazy(() => import('../components/editor/EditorLayout').then(module => ({ default: module.EditorLayout })));
const DashboardGUI = lazy(() => import('../components/gui/dashboard/DashboardGUI'));
const ModelManagementGUI = lazy(() => import('../components/gui/modelManagement/ModelManagementGUI'));
const TaskManagementGUI = lazy(() => import('../components/gui/taskManagement/TaskManagementGUI'));
const TeamsAgentsGUI = lazy(() => import('../components/gui/teamsAgents/TeamsAgentsGUI'));

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);

  // Load theme from localStorage on mount
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    const saved = localStorage.getItem('app-theme');
    return saved === 'dark';
  });

  // Apply theme on mount
  useEffect(() => {
    if (isDarkTheme) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Theme toggle handler with localStorage persistence
  const toggleTheme = () => {
    const newTheme = !isDarkTheme;
    setIsDarkTheme(newTheme);
    
    if (newTheme) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('app-theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('app-theme', 'light');
    }
  };

  // Navigation handler
  const handleNavigate = (path: string) => {
    navigate(path);
  };



  // Auto close right side chat panel when on dedicated chat page
  useEffect(() => {
    if (location.pathname === '/chat' || location.pathname.startsWith('/c/')) {
      setChatPanelOpen(false);
    }
  }, [location.pathname]);

  const PageHeaderWrapper = () => {
    const { leftContent } = usePageHeader();
    
    return (
      <TopNavBar
        isDarkTheme={isDarkTheme}
        onToggleTheme={toggleTheme}
        leftContent={leftContent}
      />
    );
  };

  return (
    <NotificationProvider>
      <PersonalizationProvider>
        <PageHeaderProvider>
      <div className="h-screen w-screen flex bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 overflow-hidden">
      <LeftSidebar
        open={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onNavigate={handleNavigate}
        activePath={location.pathname}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <CollaborationProvider>
        <WebLlmProvider>
         <PageHeaderWrapper />

        <div className="flex flex-1 overflow-hidden">

         {/* Main Canvas Area */}
         <main className="flex-1 overflow-auto relative">
          <Suspense fallback={<div className="h-full flex items-center justify-center">Loading...</div>}>
            <Routes>
              <Route path="/settings/theme" element={<ThemeBuilder />} />
              <Route path="/editor" element={<EditorLayout />} />
              <Route path="/dashboard" element={<DashboardGUI />} />
              <Route path="/models" element={<ModelManagementGUI />} />
              <Route path="/tasks" element={<TaskManagementGUI />} />
              <Route path="/teams" element={<TeamsAgentsGUI />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/c/:chatId" element={<Chat />} />
              <Route path="/" element={<DashboardGUI />} />
              <Route path="*" element={
                <div className="h-full w-full flex items-center justify-center">
                  <div className="text-center text-gray-400 dark:text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                    </svg>
                    <h2 className="text-xl font-medium mb-2">Main Canvas</h2>
                    <p className="text-sm">Current view: {location.pathname}</p>
                  </div>
                </div>
              } />
            </Routes>
          </Suspense>

           <FloatingChatButton
             onClick={() => setChatPanelOpen(true)}
             visible={!chatPanelOpen && location.pathname !== '/' && location.pathname !== '/chat' && !location.pathname.startsWith('/c/')}
           />
        </main>

          <ChatPanel
            isOpen={chatPanelOpen}
            onClose={() => setChatPanelOpen(false)}
          />
        </div>

         <footer>
           <StatusBar
             currentView="editor"
             onNavigate={handleNavigate}
             onToggleChatPanel={() => setChatPanelOpen(prev => !prev)}
             isChatPanelOpen={chatPanelOpen}
           />
         </footer>
        </WebLlmProvider>
        </CollaborationProvider>
      </div>
    </div>
        </PageHeaderProvider>
      </PersonalizationProvider>
    </NotificationProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/*" element={<AppContent />} />
      </Routes>
    </BrowserRouter>
  );
}

export { App };
